import { useState, useEffect, useRef } from 'react';
import { type Language } from '../i18n';

interface HeroStatsProps {
  potentialSavings: number;
  currency: string;
  optimizationPercent: number;
  language: Language;
}

function useCountUp(target: number, duration: number = 2000): number {
  const [current, setCurrent] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    startTimeRef.current = null;

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function - ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = Math.floor(eased * target);

      setCurrent(value);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [target, duration]);

  return current;
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: currency || 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    // Fallback for invalid currency codes
    return `€${amount.toLocaleString('it-IT')}`;
  }
}

export function HeroStats({ potentialSavings, currency, optimizationPercent, language }: HeroStatsProps) {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Trigger animation after mount
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const animatedSavings = useCountUp(isVisible ? potentialSavings : 0, 2500);
  const animatedPercent = useCountUp(isVisible ? optimizationPercent : 0, 2000);

  return (
    <div
      ref={containerRef}
      className={`hero-stats relative overflow-hidden rounded-2xl bg-primary-50 dark:bg-primary-50/20 p-8 mb-6 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {/* Animated background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="hero-glow absolute -top-1/2 -left-1/2 w-full h-full bg-primary-100/40 rounded-full blur-3xl animate-pulse" />
        <div className="hero-glow absolute -bottom-1/2 -right-1/2 w-full h-full bg-primary-100/30 rounded-full blur-3xl animate-pulse delay-500" />
      </div>

      {/* Sparkle particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {isVisible && (
          <>
            <div className="sparkle absolute top-1/4 left-1/4" style={{ animationDelay: '0s' }} />
            <div className="sparkle absolute top-1/3 right-1/3" style={{ animationDelay: '0.3s' }} />
            <div className="sparkle absolute bottom-1/4 left-1/2" style={{ animationDelay: '0.6s' }} />
            <div className="sparkle absolute top-1/2 right-1/4" style={{ animationDelay: '0.9s' }} />
            <div className="sparkle absolute bottom-1/3 right-1/2" style={{ animationDelay: '1.2s' }} />
          </>
        )}
      </div>

      {/* Content */}
      <div className="relative z-10 text-center">
        <p className="text-sm font-medium text-muted-foreground mb-2 uppercase tracking-wider">
          {language === 'it' ? 'Risparmio Annuale Potenziale' : 'Potential Annual Savings'}
        </p>

        {/* Main number */}
        <div className="relative inline-block">
          <span
            className={`hero-number text-5xl sm:text-6xl md:text-7xl font-bold text-primary transition-all duration-300 ${
              isVisible ? 'animate-number-glow' : ''
            }`}
          >
            {formatCurrency(animatedSavings, currency)}
          </span>
        </div>

        {/* Optimization badge */}
        <div className={`mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 dark:bg-green-500/20 rounded-full transition-all duration-500 delay-300 ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
        }`}>
          <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          <span className="text-sm font-semibold text-green-700 dark:text-green-400">
            {animatedPercent}% {language === 'it' ? 'potenziale di ottimizzazione' : 'optimization potential'}
          </span>
        </div>
      </div>
    </div>
  );
}
